import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DownloadPill } from './DownloadPill';
import { drawCaption, recordedExt, saveBlob, startRecording, type Recording } from './videoRecorder';
import { useLanguage } from '../../contexts/LanguageContext';
import { ClipStage, READER_FRAME, drawClipFrame, enterMs } from './ClipPlayer';
import { SHOT_BOX, useFormFactor } from './formFactor';
import { canExport, frameEncoder } from './videoExport';
import { CaptionText } from './CaptionText';
import { ScrubBar, nextSpeed } from './ScrubBar';
import { CLIP_SPEED, captionAt, clipAssembly, clipMs, finishedClip, useClips, useShownSlots, type Clip } from './assemblyClip';
import { downloadAssemblyGlb, sharedMeshes } from './assemblyGlb';
import { useProductLabel } from './ProductColumn';
import type { Slot } from './assemblySlots';
import { subStepLabel } from './stepsRun';
import type { AssemblyView, CamPose } from './assemblyScene';

type State = 'loading' | 'ready' | 'failed';
// One thing played: a sub-step's clip, or the product assembled it ends on (`end`). The first
// sub-step opens on the whole of its parts itself (see Fitting.opensWide).
type Item = { key: string; clip: Clip; slot: Slot | null; kind: 'step' | 'end' };
const END = '__finished';

// THE WHOLE ASSEMBLY, START TO FINISH: every answered sub-step's clip played one after another, as
// the manual now stands — signed off or not (see useClips' `unsigned`) — and then the product
// assembled, as the End view shows it. A sub-step with nothing answered is passed over, and said to be.
//
// IN THE VIEWER EVERY SUB-STEP IS SEEN IN: the picture in its frame, the sentence's beat over its
// foot with its number (see captionAt), and one bar under it for the whole — play, ×2, the scrubber.
//
// AND WITHOUT A BREAK BETWEEN TWO: the next one's scene is built behind the one playing, and takes
// its place on its last frame once it is ready (the last frame held until then, never a blank and a
// spinner); its camera flies in from the last shot (see ClipStage's `enterFrom`).
export function AllClips({ docId }: { docId: string }) {
  const formFactor = useFormFactor();
  const { t: tr, language } = useLanguage();
  const slots = useShownSlots(docId).filter((s) => s.step != null);
  const clips = useClips(docId, true);
  const [at, setAt] = useState(0);
  const [t, setT] = useState(0);
  const [seek, setSeek] = useState<number | null>(null);
  // STILL UNTIL PLAY IS PRESSED, like a sub-step.
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  // A VIDEO OF IT BEING RECORDED (see record): the whole played from the first, as it plays.
  const [recording, setRecording] = useState<Recording | null>(null);
  // ASKED FOR, and waiting for the first sub-step to be on screen and built before it starts: started
  // at once from the end, its first frames were the product assembled.
  const [toRecord, setToRecord] = useState(false);
  // OR BEING MADE FRAME BY FRAME (see exportAll): how far, 0 to 1; null when not.
  const [exporting, setExporting] = useState<number | null>(null);
  const cancelled = useRef(false);
  // OR THE ASSEMBLY AS AN ANIMATED GLB BEING RECORDED (see exportGlb): how far, 0 to 1; null when not.
  const [glbAt, setGlbAt] = useState<number | null>(null);
  const label = useProductLabel(docId);
  // What the recorder draws each frame, read when it draws (see record).
  const live = useRef<{ key: string; text: string; beat: string | null }>({ key: '', text: '', beat: null });
  // Each stage's state, by key; and whether the one playing is over and waits for the next.
  const [states, setStates] = useState<Record<string, State>>({});
  const statesRef = useRef(states);
  statesRef.current = states;
  const [over, setOver] = useState(false);
  // Whether the one playing came from the one before (and flies in from its shot).
  const [followed, setFollowed] = useState(false);
  const viewers = useRef<Record<string, AssemblyView | null>>({});
  // WHERE THE CAMERA WAS WHEN THE LAST ONE ENDED, carried into the next one's frame (see carry).
  const [enter, setEnter] = useState<CamPose | null>(null);
  const playable = slots.filter((s) => clips[subStepLabel(s.step!, s.letter)]);
  const steps: Item[] = playable.map((s) => ({ key: s.key, clip: clips[subStepLabel(s.step!, s.letter)], slot: s, kind: 'step' }));
  const last = steps[steps.length - 1];
  // THE BAR'S STRETCHES: every sub-step; the end is its far end.
  const bar: Item[] = steps;
  const items: Item[] = last ? [...bar, { key: END, clip: finishedClip(last.clip), slot: null, kind: 'end' }] : [];
  const n = Math.min(at, items.length - 1);
  const item = items[n];
  const next = items[n + 1];
  const ready = (i: Item | undefined) => !!i && states[i.key] === 'ready';
  const go = (k: number, follow: boolean) => {
    setAt(k); setT(0); setSeek(null); setOver(false); setFollowed(follow);
  };
  // THE CAMERA AS THE LAST ONE LEFT IT, IN THE NEXT ONE'S FRAME. Each clip stands its parts where
  // its own placing put them, so the same chair is turned or moved from one clip to the next, and
  // a camera carried over by its numbers looked at it from somewhere else (1-1 to 1-2 swung round).
  // Carried through a mesh both scenes hold where it stays still — the next one's fixed piece,
  // which the last one had standing as its fixed part or its moving one set home: where it is in
  // each is the change of frame.
  const carry = (from: Item, to: Item): CamPose | null => {
    const pv = viewers.current[from.key];
    const nv = viewers.current[to.key];
    if (!pv || !nv) return null;
    const pose = pv.cameraPose();
    for (const url of sharedMeshes(to.clip)) {
      const a = pv.meshFrame(url, ['target', 'moving']);
      const b = nv.meshFrame(url, ['target']);
      if (a && b) return nv.carryPose(pose, a, b);
    }
    return null;
  };
  // ON TO THE NEXT ONCE BOTH THE ONE PLAYING IS OVER AND THE NEXT IS BUILT.
  useEffect(() => {
    if (!(over && next && ready(next))) return;
    setEnter(carry(item, next));
    go(n + 1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, next?.key, states]);
  // THE RECORDING STARTS ON THE FIRST SUB-STEP'S FIRST FRAME, once it is built.
  useEffect(() => {
    if (!toRecord || n !== 0 || !ready(items[0])) return;
    setToRecord(false);
    begin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toRecord, n, states]);
  if (!item) return <p className="text-center text-sm text-slate-400">{tr('notice.assembly.allNone')}</p>;
  const slot = item.slot;
  const text = slot ? (language === 'fr' ? slot.textFr : slot.textEn) : tr('notice.public.doneText');
  const beat = slot
    ? captionAt(language === 'fr' ? slot.beatsFr : slot.beatsEn, text, item.clip.movement, t, slot.beatItems)
    : { text, index: 0, count: 1 };
  const skipped = slots.length - playable.length;
  // THE TIMER: every sub-step's own length at the speed chosen, and how much of the whole is behind.
  const lengthOf = (i: Item) => clipMs(i.clip.movement) / CLIP_SPEED / speed;
  // And the camera's glide into each one after the first, which plays nothing (see enterMs).
  const glide = enterMs(speed);
  const total = bar.reduce((sum, i) => sum + lengthOf(i), 0) + Math.max(0, bar.length - 1) * glide;
  const done = item.kind === 'end' ? total
    : bar.slice(0, n).reduce((sum, i) => sum + lengthOf(i), 0) + n * glide + t * lengthOf(item);
  const clock = (ms: number) => {
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  live.current = { key: item.key, text: ready(item) ? beat.text : '', beat: slot && beat.count > 1 ? `${beat.index + 1}/${beat.count}` : null };
  // RECORDING: each frame the one playing, and its caption.
  const record = () => {
    setPlaying(false);
    go(0, false);
    setSeek(0);
    setToRecord(true);
  };
  // FULL HD, each clip rendered at that size while it is recorded (see renderWidth), not scaled up
  // from the box on screen.
  const begin = () => {
    const [w, h] = [1920, 1080];
    const sharp = new Set<string>();
    setRecording(startRecording(w, h, (ctx) => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      const v = viewers.current[live.current.key];
      if (v && !sharp.has(live.current.key)) { sharp.add(live.current.key); v.renderWidth(w); }
      v?.drawInto(ctx, w, h);
      drawCaption(ctx, w, h, live.current.text, live.current.beat);
    }));
    setPlaying(true);
  };
  // THE VIDEO MADE FRAME BY FRAME, where the browser can encode (see videoExport): every item in
  // turn, on screen and built before its first frame is drawn, then each frame drawn at its own
  // moment — the camera's glide in from the last one, then the clip at the speed chosen — with its
  // caption, and encoded at that time. Nothing is played: a slow frame costs the export time only.
  const FPS = 30;
  const exportAll = async () => {
    const [w, h] = [1920, 1080];
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    const waitFor = async (ok: () => boolean) => {
      while (!ok()) {
        if (cancelled.current) throw new Error('cancelled');
        await new Promise((r) => setTimeout(r, 50));
      }
    };
    const built = (i: Item) => () => statesRef.current[i.key] === 'ready' && !!viewers.current[i.key];
    const lengths = items.map((i, k) => clipMs(i.clip.movement) / CLIP_SPEED / speed + (k ? enterMs(speed) : 0));
    const frames = lengths.reduce((sum, ms) => sum + Math.max(1, Math.round((ms * FPS) / 1000)), 0);
    cancelled.current = false;
    setPlaying(false);
    setExporting(0);
    let frameNo = 0;
    let from: CamPose | null = null;
    try {
      const encoder = await frameEncoder(w, h, FPS);
      for (const [k, it] of items.entries()) {
        go(k, false);
        await waitFor(built(it));
        const v = viewers.current[it.key]!;
        v.renderWidth(w);
        const asm = clipAssembly(it.clip, true);
        const glideMs = k && from ? enterMs(speed) : 0;
        const clipLen = clipMs(it.clip.movement) / CLIP_SPEED / speed;
        const count = Math.max(1, Math.round(((glideMs + clipLen) * FPS) / 1000));
        for (let f = 0; f < count; f += 1) {
          if (cancelled.current) throw new Error('cancelled');
          // The last frame of each is its end, so the next one's glide starts from where it stopped.
          const ms = count > 1 ? (f / (count - 1)) * (glideMs + clipLen) : glideMs + clipLen;
          const at = ms < glideMs ? 0 : Math.min(1, (ms - glideMs) / clipLen);
          drawClipFrame(v, asm, it.clip, at, from && glideMs && ms < glideMs ? { from, k: ms / glideMs } : null);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          v.drawInto(ctx, w, h);
          const s = it.slot;
          const said = s ? (language === 'fr' ? s.textFr : s.textEn) : tr('notice.public.doneText');
          const b = s ? captionAt(language === 'fr' ? s.beatsFr : s.beatsEn, said, it.clip.movement, at, s.beatItems) : null;
          drawCaption(ctx, w, h, b ? b.text : said, b && b.count > 1 ? `${b.index + 1}/${b.count}` : null);
          await encoder.add(canvas, frameNo);
          frameNo += 1;
          if (frameNo % 10 === 0) setExporting(frameNo / frames);
        }
        // THE CAMERA CARRIED INTO THE NEXT ONE'S FRAME, once that one is built (see carry).
        const nx = items[k + 1];
        if (nx) {
          await waitFor(built(nx));
          from = carry(it, nx);
        }
        v.renderWidth(null);
      }
      saveBlob(await encoder.finish(), `${tr('notice.assembly.allFile')}.mp4`);
    } catch (e) {
      if (!cancelled.current) console.error('video export failed', e);
    } finally {
      Object.values(viewers.current).forEach((v) => v?.renderWidth(null));
      setExporting(null);
    }
  };
  // THE WHOLE ASSEMBLY AS A 3D FILE (see assemblyGlb): every sub-step, the camera nowhere in it.
  const exportGlb = async () => {
    const waitFor = async (ok: () => boolean) => {
      while (!ok()) {
        if (cancelled.current) throw new Error('cancelled');
        await new Promise((r) => setTimeout(r, 50));
      }
    };
    cancelled.current = false;
    setPlaying(false);
    setGlbAt(0);
    try {
      await downloadAssemblyGlb({
        clips: steps.map((s) => s.clip),
        name: `${label} (${tr('notice.assembly.allFile')})`,
        open: (k) => go(k, false),
        viewer: async (k) => {
          const key = steps[k].key;
          await waitFor(() => statesRef.current[key] === 'ready' && !!viewers.current[key]);
          return viewers.current[key]!;
        },
        stopped: () => cancelled.current,
        onProgress: setGlbAt,
      });
    } catch (e) {
      if (!cancelled.current) console.error('assembly GLB failed', e);
    } finally {
      setGlbAt(null);
    }
  };
  const finish = async () => {
    const was = recording;
    if (!was) return;
    setRecording(null);
    setPlaying(false);
    Object.values(viewers.current).forEach((v) => v?.renderWidth(null));
    saveBlob(await was.stop(), `${tr('notice.assembly.allFile')}.${was.ext}`);
  };
  // THE TWO FILES, one above the other, left of the picture (Louis 2026-10-29): the video — made
  // frame by frame where the browser can (see exportAll), recorded as it plays where not; pressed
  // again while it is made, it stops — and the assembly in 3D (see exportGlb). Each says its format.
  const videoBusy = exporting != null || !!recording;
  const files = (
    <div className="flex flex-col items-center gap-2">
      <DownloadPill
        busy={videoBusy}
        onClick={() => {
          if (exporting != null) { cancelled.current = true; return; }
          if (recording) { void finish(); return; }
          if (canExport()) void exportAll(); else record();
        }}
      >
        {exporting != null
          ? tr('notice.assembly.allExporting').replace('{n}', String(Math.round(exporting * 100)))
          : recording ? tr('notice.assembly.allRecording')
          : tr('notice.assembly.allDownload').replace('{format}', (canExport() ? 'mp4' : recordedExt()).toUpperCase())}
      </DownloadPill>
      <DownloadPill busy={glbAt != null} onClick={() => { if (glbAt != null) cancelled.current = true; else if (!videoBusy) void exportGlb(); }}>
        {glbAt != null
          ? tr('notice.assembly.allGlbMaking').replace('{n}', String(Math.round(glbAt * 100)))
          : tr('notice.assembly.allGlb')}
      </DownloadPill>
    </div>
  );
  const stage = (i: Item, on: boolean) => (
    <div key={i.key} className={`absolute inset-0 ${on ? '' : 'pointer-events-none opacity-0'}`}>
      <ClipStage
        clip={i.clip}
        playing={on && playing && !over && ready(i)}
        onState={(st) => setStates((was) => (was[i.key] === st ? was : { ...was, [i.key]: st }))}
        onT={on && i.kind !== 'end' ? setT : undefined}
        seek={on ? seek : null}
        once
        speed={speed}
        enterFrom={on && followed ? enter : null}
        onViewer={(v) => { viewers.current[i.key] = v; }}
        // The product assembled stops there; any other waits for the next (see above).
        onEnd={() => { if (on) { if (next) setOver(true); else { setPlaying(false); void finish(); } } }}
      />
    </div>
  );
  return (
    // THE PICTURE BETWEEN TWO COLUMNS on a wide screen, as Phase 5's viewer stands: the 3D download
    // in the left one.
    <div className="mx-auto flex w-full max-w-[86rem] flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
      {/* HOW FAR THROUGH THE WHOLE, and which sub-step that is (at the end, that it is done). */}
      <div className="flex justify-center leading-tight">
        <div className="flex flex-col items-center">
          {slot ? (
            <>
              <p className="text-sm font-semibold text-slate-700">{n + 1}/{steps.length}</p>
              <p className="text-sm font-semibold text-slate-700">({slot.label})</p>
            </>
          ) : (
            <p className="text-sm font-semibold text-slate-700">{tr('notice.public.doneTitle')}</p>
          )}
        </div>
      </div>
      {/* THE FILES (see `files`), in the room on the picture's left, half way up, as Phase 5's download
          stands (Louis 2026-10-29); under it where there is no such room. */}
      <div className="flex flex-col xl:grid xl:grid-cols-[14.25rem_minmax(0,1fr)_14.25rem] xl:items-center xl:gap-4">
      <div className="hidden justify-center xl:flex">
        {files}
      </div>
      {/* A SUB-STEP'S OWN BOX (Louis 2026-11-02): its shape, mobile or desktop (see formFactor), and the
          reader's dress. What is recorded is what is seen (see record), in that shape. */}
      <div className={`relative mx-auto max-w-full overflow-hidden ${SHOT_BOX[formFactor]} ${READER_FRAME}`}>
        {/* THE ONE PLAYING, AND THE NEXT BUILT BEHIND IT: kept by key, so the next is not built
            again when it comes on. */}
        {[item, ...(next ? [next] : [])].map((i) => stage(i, i === item))}
        {!ready(item) && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}
        {ready(item) && text && (
          <div className="pointer-events-none absolute inset-x-16 bottom-3 flex justify-center">
            <CaptionText text={beat.text} beat={slot ? beat : undefined} />
          </div>
        )}
      </div>
      <div className="hidden xl:block" />
      </div>
      <div className="flex justify-center xl:hidden">
        {files}
      </div>
      {/* ONE BAR FOR THE WHOLE ASSEMBLY, each sub-step an equal stretch of it, named inside its own:
          a hand anywhere on it goes to that sub-step, at that moment of it. At its end, the end. */}
      {/* AND HOW LONG, on the right: where the whole is, of how long it lasts at the speed chosen. */}
      <div className="flex items-center gap-3 xl:mx-[15.25rem]">
      <div className="min-w-0 flex-1">
      <ScrubBar
        t={item.kind === 'end' ? 1 : (n + t) / bar.length}
        // Numbered through the whole, as the header counts them.
        sections={bar.map((_, k) => String(k + 1))}
        playing={playing}
        speed={speed}
        // THE SAME TWO BUTTONS AS A SUB-STEP'S: play or pause, and the speed. Play on the end
        // starts again from the first.
        onToggle={() => { if (item.kind === 'end' && !playing) go(0, false); setPlaying((p) => !p); setSeek(null); }}
        onSpeed={() => setSpeed(nextSpeed)}
        onSeek={(to) => {
          const k = Math.min(bar.length - 1, Math.floor(to * bar.length));
          const local = Math.min(1, to * bar.length - k);
          setPlaying(false);
          if (k !== n) go(k, false);
          setOver(false);
          setSeek(local);
          setT(local);
        }}
      />
      </div>
      <span className="shrink-0 text-xs tabular-nums text-slate-500">{clock(done)} / {clock(total)}</span>
      </div>
      {skipped > 0 && (
        <p className="text-center text-xs text-amber-700">{tr('notice.assembly.allSkipped').replace('{n}', String(skipped))}</p>
      )}
    </div>
  );
}
